import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { redirectToLogin } from '@/helpers/auth'
import { toast } from 'react-toastify'
import { withSentry } from '@/lib/monitoring/sentry-loader'
import i18n from '@/i18n/config'
import { getTokenRefresher, setTokenRefresher } from './token-refresh'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: AxiosResponse,
    public code?: string,
    public config?: InternalAxiosRequestConfig,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  isForbidden() {
    return this.status === 403
  }
  isUnauthorized() {
    return this.status === 401
  }
  isNotFound() {
    return this.status === 404
  }
  isServerError() {
    return this.status !== undefined && this.status >= 500
  }
}

export const axiosClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 0,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

setTokenRefresher(async () => {
  await refreshClient.post('/auth/refresh-token')
})

let isRefreshing = false
let refreshSubscribers: Array<(success: boolean) => void> = []
const forbiddenRequestSignatures = new Set<string>()
const FORBIDDEN_TOAST_COOLDOWN_MS = 3000
const forbiddenToastTimestamps = new Map<string, number>()

function subscribeToRefresh(cb: (success: boolean) => void) {
  refreshSubscribers.push(cb)
}

function notifyRefreshSubscribers(success: boolean) {
  refreshSubscribers.forEach((cb) => cb(success))
  refreshSubscribers = []
}

function extractErrorMessage(err: AxiosError | ApiError): string {
  const status = err?.response?.status
  const data = err?.response?.data as
    | string
    | {
        message?: string
        errors?: { message?: string; code?: string }[]
        error?: string | { message?: string }
      }
    | undefined

  const fallback =
    status === 403
      ? 'You do not have permission to perform this action.'
      : status === 404
        ? 'The requested resource could not be found.'
        : status && status >= 500
          ? 'Server error. Please try again later.'
          : 'Something went wrong. Please try again.'

  if (!data) return fallback
  if (typeof data === 'string') return data
  if (data.message) return data.message

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const e = data.errors[0]
    return e.message || e.code || JSON.stringify(e)
  }

  if (data.error) {
    return typeof data.error === 'string'
      ? data.error
      : data.error.message || JSON.stringify(data.error)
  }

  return fallback
}

function normalizeUrlPath(url: string): string {
  const u = (url ?? '').toLowerCase().split('?')[0]
  return u.startsWith('/') ? u : `/${u}`
}

const TOAST_MESSAGE_OVERRIDES: Array<{ suffix: string; key: string }> = [
  { suffix: '/revoke', key: 'ui.toast.revokedSuccess' },
  { suffix: '/resend', key: 'ui.toast.resentSuccess' },
  { suffix: '/reject', key: 'ui.toast.rejectedSuccess' },
  { suffix: '/kick', key: 'ui.toast.removedSuccess' },
  { suffix: '/reinstate', key: 'ui.toast.reinstatedSuccess' },
  { suffix: '/reset-rejection-count', key: 'ui.toast.updatedSuccess' },
  { suffix: '/cancel', key: 'ui.toast.cancelledSuccess' },
  {
    suffix: '/UpdateAltaTitleDocuments',
    key: 'adminCheck.altaTitle.updateSuccess',
  },
]

function resolveToastOverrideKey(url: string): string | undefined {
  const u = normalizeUrlPath(url)
  if (u.startsWith('/admin/city-members/') && u.endsWith('/roles')) {
    return 'ui.toast.updatedSuccess'
  }
  return TOAST_MESSAGE_OVERRIDES.find((o) => u.endsWith(o.suffix))?.key
}

function requestPathIncludes(url: string, fragment: string): boolean {
  const u = normalizeUrlPath(url)
  const f = normalizeUrlPath(fragment)
  return u === f || u.startsWith(`${f}/`)
}

function getRequestSignature(config: InternalAxiosRequestConfig): string {
  const method = (config.method || 'get').toLowerCase()
  const url = normalizeUrlPath(config.url ?? '')
  return `${method}:${url}`
}

export function clearForbiddenCache(): void {
  forbiddenRequestSignatures.clear()
  forbiddenToastTimestamps.clear()
}

function maybeShowForbiddenToast(
  signature: string,
  message: string,
  method: string,
): void {
  if (method === 'get' || method === 'head' || method === 'options') {
    return
  }

  const now = Date.now()
  const lastShownAt = forbiddenToastTimestamps.get(signature) ?? 0

  if (now - lastShownAt >= FORBIDDEN_TOAST_COOLDOWN_MS) {
    toast.error(message)
    forbiddenToastTimestamps.set(signature, now)
  }
}

const TOAST_SKIP_URLS = [
  '/Attachments/get-presigned-upload-url',
  '/auth',
  'user/me',
  '/user/me',
  '/Template/List',
  '/CommentReplies/replies',
  '/Template/Versions',
  '/contact-us',
  '/reports/export',
  '/reports/templates/user',
  '/v1/Annotations',
  '/v1/Drawings',
  '/admin/city-roles/ensure',
  '/admin/membership-state/repair',
  '/QcFindings/Review',
]

axiosClient.interceptors.request.use((config) => {
  const signature = getRequestSignature(config)
  const method = (config.method || 'get').toLowerCase()
  if (forbiddenRequestSignatures.has(signature)) {
    maybeShowForbiddenToast(signature, i18n.t('ui.toast.noPermission'), method)
    return Promise.reject(
      new ApiError(
        i18n.t('ui.toast.noPermission'),
        403,
        undefined,
        'ERR_FORBIDDEN_CACHED',
        config,
      ),
    )
  }
  return config
})

axiosClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const signature = getRequestSignature(response.config)
    forbiddenRequestSignatures.delete(signature)
    forbiddenToastTimestamps.delete(signature)

    const method = (response.config.method || 'get').toLowerCase()
    const url = response.config.url ?? ''
    const skipToast = TOAST_SKIP_URLS.some((path) =>
      requestPathIncludes(url, path),
    )

    if (!skipToast) {
      const overrideKey = resolveToastOverrideKey(url)
      if (overrideKey) toast.success(i18n.t(overrideKey))
      else if (method === 'post')
        toast.success(i18n.t('ui.toast.createdSuccess'))
      else if (method === 'put')
        toast.success(i18n.t('ui.toast.updatedSuccess'))
      else if (method === 'delete')
        toast.success(i18n.t('ui.toast.deletedSuccess'))
    }

    if (response.config.responseType === 'blob') {
      return response
    }

    const data = response.data
    if (
      data &&
      typeof data === 'object' &&
      'data' in data &&
      'isSuccess' in data &&
      'errors' in data
    ) {
      return data.data
    }

    return data
  },
  async (err: unknown) => {
    if (err instanceof ApiError) {
      throw err
    }

    if (!axios.isAxiosError(err)) {
      toast.error(i18n.t('ui.toast.unexpectedError'))
      throw new ApiError(i18n.t('ui.toast.unexpectedError'))
    }

    const error = err as AxiosError
    const originalRequest = (error.config ||
      {}) as InternalAxiosRequestConfig & {
      _retry?: boolean
      _retryCount?: number
      meta?: {
        handle404?: 'redirect' | 'ignore'
        retry?: boolean
        skipAuthRefresh?: boolean
      }
    }

    const status = error.response?.status
    const url = originalRequest.url ?? ''

    if (error.code === 'ECONNABORTED') {
      throw new ApiError(
        'Request timed out. Please try again.',
        undefined,
        error.response,
        error.code,
        error.config,
      )
    }
    if (error.code === 'ERR_NETWORK') {
      throw new ApiError(
        'Network error. Please check your connection and try again.',
        status,
        error.response,
        error.code,
        error.config,
      )
    }

    const skipRefreshEndpoints = [
      'auth/authenticate',
      'auth/refresh-token',
      'auth/refresh',
      'auth/logout',
    ]

    if (status === 401) {
      if (originalRequest.meta?.skipAuthRefresh) {
        throw new ApiError(
          extractErrorMessage(error),
          status,
          error.response,
          error.code,
          error.config,
        )
      }

      if (skipRefreshEndpoints.some((path) => requestPathIncludes(url, path))) {
        await redirectToLogin()
        throw new ApiError(
          extractErrorMessage(error),
          status,
          error.response,
          error.code,
          error.config,
        )
      }

      if (!originalRequest._retry) {
        originalRequest._retry = true

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeToRefresh((success) => {
              if (success) resolve(axiosClient(originalRequest))
              else
                reject(
                  new ApiError('Session expired. Please log in again.', 401),
                )
            })
          })
        }

        isRefreshing = true
        try {
          const refresh = getTokenRefresher()
          if (!refresh) throw new Error('No token refresher registered')
          await refresh()
          clearForbiddenCache()
          notifyRefreshSubscribers(true)
          return axiosClient(originalRequest)
        } catch {
          notifyRefreshSubscribers(false)
          await redirectToLogin()
          return Promise.reject(
            new ApiError('Session expired. Please log in again.', 401),
          )
        } finally {
          isRefreshing = false
        }
      } else {
        await redirectToLogin()
        return Promise.reject(
          new ApiError('Session expired. Please log in again.', 401),
        )
      }
    }

    if (status === 404) {
      throw new ApiError(
        extractErrorMessage(error),
        status,
        error.response,
        error.code,
        error.config,
      )
    }

    if (status === 403) {
      const signature = getRequestSignature(originalRequest)
      forbiddenRequestSignatures.add(signature)
      const method = (originalRequest.method || 'get').toLowerCase()
      maybeShowForbiddenToast(signature, extractErrorMessage(error), method)
    }

    const method = (originalRequest.method || 'get').toLowerCase()
    const idempotent = ['get', 'head', 'options'].includes(method)
    const allowRetry = originalRequest.meta?.retry === true || idempotent
    const MAX_RETRIES = 3

    if (
      status &&
      status >= 500 &&
      allowRetry &&
      (originalRequest._retryCount ?? 0) < MAX_RETRIES
    ) {
      originalRequest._retryCount = (originalRequest._retryCount ?? 0) + 1
      const delay = Math.pow(2, originalRequest._retryCount) * 1000
      await new Promise((r) => setTimeout(r, delay))
      return axiosClient(originalRequest)
    }

    const apiError = new ApiError(
      extractErrorMessage(error),
      status,
      error.response,
      error.code,
      error.config,
    )

    if (status !== undefined && status >= 500) {
      withSentry((Sentry) => {
        Sentry.addBreadcrumb({
          category: 'api',
          message: `${(originalRequest.method ?? 'GET').toUpperCase()} ${url} → ${status}`,
          level: 'error',
          data: { status, url, method: originalRequest.method },
        })
        Sentry.captureException(apiError, {
          extra: { url, method: originalRequest.method, status },
          fingerprint: ['api-server-error', String(status), url],
        })
      })
    }

    throw apiError
  },
)

export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosClient.request<T>({ url, method: 'GET', ...config }) as Promise<T>,
  post: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> =>
    axiosClient.request<T>({
      url,
      method: 'POST',
      data: data ?? null,
      ...config,
    }) as Promise<T>,
  put: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> =>
    axiosClient.request<T>({
      url,
      method: 'PUT',
      data: data ?? null,
      ...config,
    }) as Promise<T>,
  patch: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> =>
    axiosClient.request<T>({
      url,
      method: 'PATCH',
      data: data ?? null,
      ...config,
    }) as Promise<T>,
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosClient.request<T>({ url, method: 'DELETE', ...config }) as Promise<T>,
}
